// UserPeek — the user peek body, rendered by the global PeekHost from
// `?peek=users:<id>` (ADR-036 §2). Fetches the user DETAIL (PeekHost is global;
// it cannot rely on a list being loaded) and renders THE platform peek grammar:
//   • boxed `DetailSection dense` sections mirroring the detail page's Profile +
//     the Access card condensed (ADR-050 §2 — the role and what it allows, the
//     scope summary, the places answered for; share the primitives, never the
//     content — §Section-body);
//   • the ACTS composed by the panel from the person's ONE registry
//     (`useUserActions`, ADR-036 §7): the top row = Edit access (the primary —
//     the one editor, a quiet button; no Edit menu — a person has no card edit),
//     the footer = the labelled Actions door (the web and chat verbs, the
//     lifecycle) + "Open full page"; the overlays the acts open are mounted
//     ONCE, beside the panel.

import { useTranslation } from 'react-i18next';
import type { ProjectUserDetail } from '@ems-app/schemas/users/responses';
import { useUser } from '../../hooks/useUsers.js';
import { ContextPanel } from '../../components/patterns/ContextPanel.js';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { DescriptionList, Field } from '../../components/patterns/DescriptionList.js';
import { Alert } from '../../components/ui/Alert.js';
import { PageLoader } from '../../components/ui/PageLoader.js';
import { Avatar } from '../../components/ui/Avatar.js';
import { RoleHint } from '../../components/patterns/capabilities/RoleHint.js';
import { UserStatusBadge, SignInBadge, TelegramBadge, telegramStateOf, roleLabel } from '../../components/patterns/userDisplay.js';
import { useUserActions } from './UserActions.js';
import { ScopeSummary } from './ScopeSummary.js';
import { chainWords } from './scopeWords.js';

export interface UserPeekProps {
  projectId: string;
  userId: string;
  onClose: () => void;
}

export function UserPeek({ projectId, userId, onClose }: UserPeekProps) {
  const { t } = useTranslation('users');
  const user = useUser(projectId, userId);

  if (user.isPending) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('list.title')}>
        <div className="flex min-h-[8rem] items-center justify-center">
          <PageLoader />
        </div>
      </ContextPanel>
    );
  }
  if (user.isError || !user.data) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('list.title')}>
        <Alert variant="danger">{t('list.error')}</Alert>
      </ContextPanel>
    );
  }
  return <UserPeekBody projectId={projectId} user={user.data} onClose={onClose} />;
}

// The loaded peek — its own component so the acts hook runs on a person that is there.
function UserPeekBody({ projectId, user: u, onClose }: { projectId: string; user: ProjectUserDetail; onClose: () => void }) {
  const { t } = useTranslation('users');
  const { acts, overlays } = useUserActions(projectId, u, { onRemoved: onClose });
  const name = `${u.first_name} ${u.last_name}`;
  const dash = <span className="text-[var(--color-text-disabled)]">—</span>;
  const tg = telegramStateOf(u.external_identity);
  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={<Avatar name={name} size="lg" />}
        title={name}
        kind={roleLabel(t, u)}
        status={<UserStatusBadge status={u.status} />}
        openFullPath={`/p/${projectId}/users/${u.user_id}`}
        acts={acts}
      >
        <DetailSection dense title={t('detail.sections.profile')}>
          <DescriptionList>
            <Field label={t('peek.username')}>
              <span dir="ltr">@{u.username}</span>
            </Field>
            <Field label={t('list.cols.email')}>{u.email ? <span dir="ltr">{u.email}</span> : dash}</Field>
            <Field label={t('detail.phone')}>{u.phone_e164 ? <span dir="ltr">{u.phone_e164}</span> : dash}</Field>
          </DescriptionList>
        </DetailSection>

        {/* The Access card, condensed (ADR-050 §2). */}
        <DetailSection dense title={t('detail.sections.access')}>
          <DescriptionList>
            <Field label={t('access.role')}>
              {u.is_project_admin ? (
                t('role.admin')
              ) : (
                <span className="inline-flex items-center gap-1">
                  {roleLabel(t, u)}
                  {/* No link to follow from a peek — the ⓘ says what the role allows. */}
                  {u.role_id != null && <RoleHint projectId={projectId} roleId={u.role_id} />}
                </span>
              )}
            </Field>
            <Field label={t('access.scope')}>
              <ScopeSummary scope={u.scope} />
            </Field>
            <Field label={t('access.answersFor')}>
              {u.responsibilities.length === 0 ? (
                <span className="text-[var(--color-text-secondary)]">{t('access.answersNothing')}</span>
              ) : (
                <ul className="space-y-0.5">
                  {u.responsibilities.map((c) => (
                    <li key={`${c.responsibility_id}-${c.scope_id}`}>{chainWords(t, c)}</li>
                  ))}
                </ul>
              )}
            </Field>
            <Field label={t('list.cols.signIn')}>
              <SignInBadge signIn={u.sign_in} />
            </Field>
            {/* Chip 2 — hidden when the project has no bot (same rule as the list column). */}
            {tg ? (
              <Field label={t('list.cols.telegram')}>
                <TelegramBadge state={tg} />
              </Field>
            ) : null}
          </DescriptionList>
        </DetailSection>
      </ContextPanel>
      {overlays}
    </>
  );
}
